import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { useAxiosAuth0 } from "@shared/config/useAxiosAuth0";
import { fetchAuditLogById } from "../requests/request";
import type { AuditLog } from "../types";

interface UseAuditLogDetailOptions {
  /**
   * Optional preloaded item (e.g. carried in `location.state` after the
   * user clicks a row in the list). When provided AND the id matches,
   * the hook skips the initial fetch and starts in `hasFetched=true` so
   * the page renders immediately.
   */
  seedData?: AuditLog | null;
  /**
   * Tenant scope, mirroring the list/`filters-options` endpoints. Sent
   * as `?tenantId=<n>` so a partner viewing a customer can resolve a
   * detail that belongs to that customer. Backend returns 403 if the
   * JWT tenant doesn't own the requested tenant.
   */
  tenantId?: number | null;
}

interface UseAuditLogDetailResult {
  data: AuditLog | null;
  isFetching: boolean;
  hasFetched: boolean;
  error: string | null;
  errorStatus: number | null;
  refetch: () => void;
}

/**
 * Fetches a single audit-log entry by id.
 *
 * Notes:
 *   - 404 / 403 are surfaced via `errorStatus` so the page can decide
 *     whether to redirect, toast, or render an inline error.
 *   - When `id` is null/undefined we keep the hook in an idle state
 *     (`isFetching=false`, `hasFetched=false`, no fetch fired).
 */
export function useAuditLogDetail(
  id: number | string | null | undefined,
  options: UseAuditLogDetailOptions = {}
): UseAuditLogDetailResult {
  const api = useAxiosAuth0();
  const { seedData = null, tenantId = null } = options;

  const seedMatchesId =
    seedData != null && id != null && String(seedData.id) === String(id);

  const [data, setData] = useState<AuditLog | null>(
    seedMatchesId ? seedData : null
  );
  const [isFetching, setIsFetching] = useState<boolean>(
    id != null && !seedMatchesId
  );
  const [hasFetched, setHasFetched] = useState<boolean>(seedMatchesId);
  const [error, setError] = useState<string | null>(null);
  const [errorStatus, setErrorStatus] = useState<number | null>(null);
  const [refreshNonce, setRefreshNonce] = useState<number>(0);

  const requestIdRef = useRef<number>(0);

  useEffect(() => {
    if (id == null) {
      setData(null);
      setIsFetching(false);
      setHasFetched(false);
      setError(null);
      setErrorStatus(null);
      return;
    }

    // Hot-cache hit from `seedData` only on the very first render.
    // Subsequent refreshes still go to the network.
    if (refreshNonce === 0 && seedMatchesId) {
      return;
    }

    const requestId = ++requestIdRef.current;
    setIsFetching(true);
    setError(null);
    setErrorStatus(null);

    fetchAuditLogById(api, id, { tenantId })
      .then((response) => {
        if (requestIdRef.current !== requestId) return;
        const { status, data: body } = response;
        if (status >= 400 || !body || body.success === false) {
          setData(null);
          setError(body?.message ?? `Request failed with status ${status}`);
          setErrorStatus(status);
          return;
        }
        setData(body.data ?? null);
      })
      .catch((err: unknown) => {
        if (requestIdRef.current !== requestId) return;
        const status = axios.isAxiosError(err)
          ? err.response?.status ?? null
          : null;
        setData(null);
        setError(extractErrorMessage(err, "Failed to load audit log"));
        setErrorStatus(status);
      })
      .finally(() => {
        if (requestIdRef.current !== requestId) return;
        setIsFetching(false);
        setHasFetched(true);
      });
    // `seedMatchesId` only matters for the initial render; intentionally
    // not in the dep array.
  }, [api, id, refreshNonce, tenantId]);

  const refetch = useCallback(() => {
    setRefreshNonce((n) => n + 1);
  }, []);

  return {
    data,
    isFetching,
    hasFetched,
    error,
    errorStatus,
    refetch,
  };
}

function extractErrorMessage(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    const body = err.response?.data as { message?: string } | undefined;
    if (body?.message) return body.message;
    if (err.message) return err.message;
  }
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}
